/**
 * Priceless Possession card — warm, archive-like keepsake presentation.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  getMarkerDisplayImage,
  POSSESSION_CATEGORY_LABELS,
  POSSESSION_PRIVACY_LABELS,
  possessionWhyLine,
} from '../../utils/memoryMarkers';
import { personHref } from '../../utils/prettyArchiveLinks';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getApiBase } from '../../utils/apiBase';
import { getDisplayName } from '../../utils/companionProfiles';

const BACKEND_URL = getApiBase().replace(/\/api$/, '');

export function PricelessPossessionCard({
  marker,
  peopleById = {},
  canEdit = false,
  onEdit,
  onDelete,
  compact = false,
}) {
  const imageUrl = getMarkerDisplayImage(marker);
  const resolvedImage = imageUrl ? resolveAssetUrl(BACKEND_URL, imageUrl) : null;
  const why = possessionWhyLine(marker);
  const connectedIds = marker.connected_person_ids?.length
    ? marker.connected_person_ids
    : marker.attachment?.person_id
      ? [marker.attachment.person_id]
      : [];

  return (
    <li
      className="group overflow-hidden rounded-xl border border-[hsl(38_14%_82%)]/90 bg-gradient-to-br from-[hsl(42_28%_99%)] to-[hsl(38_22%_94%)]/40 shadow-[0_4px_20px_-12px_rgba(28,25,23,0.15)] transition hover:border-[#8B7355]/35"
      data-testid={`priceless-possession-${marker.id}`}
    >
      <div className={`flex gap-3 ${compact ? 'p-3' : 'p-4'}`}>
        {resolvedImage ? (
          <div className={`shrink-0 overflow-hidden rounded-lg border border-[hsl(38_14%_86%)] shadow-inner ${compact ? 'h-14 w-14' : 'h-20 w-20 sm:h-24 sm:w-24'}`}>
            <img src={resolvedImage} alt="" className="h-full w-full object-cover" loading="lazy" />
          </div>
        ) : (
          <div className={`flex shrink-0 items-center justify-center rounded-lg border border-dashed border-[#c9a961]/40 bg-[#c9a961]/8 ${compact ? 'h-14 w-14' : 'h-20 w-20'}`}>
            <span className="font-serif text-lg text-[#8b7355]/70">✦</span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="mb-1 flex flex-wrap gap-1.5">
                {marker.category ? (
                  <Badge variant="secondary" className="text-[10px] font-normal bg-[#c9a961]/12 text-[#6b5f42] border-[#c9a961]/25">
                    {POSSESSION_CATEGORY_LABELS[marker.category] || marker.category}
                  </Badge>
                ) : null}
                <Badge variant="outline" className="text-[10px] font-normal gap-0.5">
                  <Lock className="h-2.5 w-2.5" />
                  {POSSESSION_PRIVACY_LABELS[marker.privacy] || 'Private'}
                </Badge>
              </div>
              <p className={`font-serif leading-snug text-[#1c1917] ${compact ? 'text-sm' : 'text-base'}`}>{marker.title}</p>
              {why ? (
                <p className={`mt-1 text-[#5c534a] ${compact ? 'text-xs line-clamp-2' : 'text-sm line-clamp-3'}`}>{why}</p>
              ) : null}
            </div>
            {canEdit && onEdit && onDelete ? (
              <div className="flex shrink-0 gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(marker)} aria-label="Edit">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(marker)} aria-label="Remove">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : null}
          </div>

          {connectedIds.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {connectedIds.map((pid) => {
                const p = peopleById[pid];
                if (!p) return null;
                return (
                  <Link
                    key={pid}
                    to={personHref(p)}
                    className="rounded-full border border-[#8B7355]/20 bg-[#8B7355]/6 px-2 py-0.5 text-[10px] text-[#5c4a32] hover:bg-[#8B7355]/12"
                  >
                    {getDisplayName(p)}
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export default PricelessPossessionCard;
